<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import AppMenuBarWithDateRange from '@/components/Menu/AppMenuBarWithDateRange.vue'
import { ArrowLeft, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { type LifecycleState } from '@/models/MetricsSnapshot'
import { Execution } from '@/shared/Executions/models/Execution'
import { ExecutionApiService } from '@/shared/Executions/services/ExecutionApiService'
import { GetExecutionStatusArgs } from '@/shared/Executions/models/GetExecutionStatusArgs'
import { InjectionService } from '@/shared/services/InjectionService'
import ExecutionRecordPanel from '@/shared/Executions/components/ExecutionRecordPanel.vue'
import ExecutionSummaryPanel from '@/shared/Executions/components/ExecutionSummaryPanel.vue'

const props = withDefaults(defineProps<{
  tenantId: string
  tenantName: string
  userId: string
  userName: string
  executionId: string
  executionIds?: string[]
  status?: string
  lifecycleState?: LifecycleState
}>(), {
  lifecycleState: 'all',
  executionIds: () => [],
})

const emit = defineEmits<{
  'navigate-to-summary': []
  'navigate-to-tenant': [payload: { tenantId: string; tenantName: string }]
  'navigate-to-user': [payload: { tenantId: string; tenantName: string; userId: string; userName: string }]
  'navigate-to-drilldown': [payload: { status?: string }]
  'navigate-to-execution': [payload: { executionId: string }]
  'update:lifecycleState': [state: LifecycleState]
}>()

// --- Service injection ---

const executionApiService: ExecutionApiService =
  InjectionService.injectStrict(ExecutionApiService.injectableKey)

// --- State ---

const execution = ref<Execution | null>(null)
const rawJson = ref<any>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const activeTab = ref<'execution' | 'summary'>('execution')
const subView = ref<'overview' | 'json'>('overview')

// --- Status label mapping ---

const STATUS_LABEL_MAP: Record<string, string> = {
  succeeded: 'Succeeded',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  cancelled: 'Cancelled',
  throttled: 'Throttled',
  rejected: 'Rejected',
}

const statusLabel = computed(() => {
  if (!props.status) return 'Total'
  return STATUS_LABEL_MAP[props.status] ?? props.status
})

const statusBadgeClass = computed(() => {
  switch (props.status) {
    case 'succeeded': return 'bg-green-500 hover:bg-green-500 text-white'
    case 'failed': return 'bg-red-500 hover:bg-red-500 text-white'
    case 'running': return 'bg-blue-500 hover:bg-blue-500 text-white'
    case 'pending': return 'bg-yellow-500 hover:bg-yellow-500 text-white'
    case 'cancelled': return 'bg-gray-500 hover:bg-gray-500 text-white'
    case 'throttled': return 'bg-orange-500 hover:bg-orange-500 text-white'
    case 'rejected': return 'bg-red-500 hover:bg-red-500 text-white'
    default: return 'bg-blue-500 hover:bg-blue-500 text-white'
  }
})

const truncatedExecutionId = computed(() => {
  return props.executionId?.substring(0, 8) ?? ''
})

// --- Data fetching ---

async function fetchExecutionDetail() {
  loading.value = true
  error.value = null

  try {
    const args = new GetExecutionStatusArgs({
      tenantId: props.tenantId,
      userId: props.userId,
      executionId: props.executionId,
    })

    const response = await executionApiService.getExecutionStatus(args)
    execution.value = response.data
    rawJson.value = response.data
  } catch (err: any) {
    error.value = err?.message ?? 'Failed to load execution details'
  } finally {
    loading.value = false
  }
}

// --- Navigation ---

function handleNavigateToSummary() {
  emit('navigate-to-summary')
}

function handleNavigateToTenant() {
  emit('navigate-to-tenant', {
    tenantId: props.tenantId,
    tenantName: props.tenantName,
  })
}

function handleNavigateToUser() {
  emit('navigate-to-user', {
    tenantId: props.tenantId,
    tenantName: props.tenantName,
    userId: props.userId,
    userName: props.userName,
  })
}

function handleNavigateToDrilldown() {
  emit('navigate-to-drilldown', { status: props.status })
}

// --- Menu bar buttons ---

const currentIndex = computed(() => {
  if (!props.executionIds.length) return -1
  return props.executionIds.indexOf(props.executionId)
})

const hasPrev = computed(() => currentIndex.value > 0)
const hasNext = computed(() => currentIndex.value >= 0 && currentIndex.value < props.executionIds.length - 1)

const positionLabel = computed(() => {
  if (currentIndex.value < 0 || !props.executionIds.length) return ''
  return `${currentIndex.value + 1} of ${props.executionIds.length}`
})

function handlePrev() {
  if (hasPrev.value) {
    emit('navigate-to-execution', { executionId: props.executionIds[currentIndex.value - 1] })
  }
}

function handleNext() {
  if (hasNext.value) {
    emit('navigate-to-execution', { executionId: props.executionIds[currentIndex.value + 1] })
  }
}

const navButtons = ref([
  {
    icon: ArrowLeft,
    onClick: handleNavigateToDrilldown,
    title: '',
    toolTip: 'Back to Drilldown',
  },
  {
    icon: RefreshCw,
    onClick: fetchExecutionDetail,
    animate: loading,
    title: '',
    toolTip: 'Refresh',
  },
])

// --- Lifecycle ---

watch(() => props.executionId, () => {
  fetchExecutionDetail()
})

onMounted(() => {
  fetchExecutionDetail()
})
</script>

<template>
  <div>
    <!-- Menu bar with back button -->
    <AppMenuBarWithDateRange
      :showSearch="false"
      :showDateRange="false"
      :buttons="navButtons"
    />

    <!-- Breadcrumb navigation -->
    <nav class="mb-4 flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToSummary"
      >
        Summary
      </button>
      <span class="mx-1">&gt;</span>
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToTenant"
      >
        {{ tenantName }}
      </button>
      <span class="mx-1">&gt;</span>
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToUser"
      >
        {{ userName }}
      </button>
      <span class="mx-1">&gt;</span>
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToDrilldown"
      >
        <Badge :class="statusBadgeClass">{{ statusLabel }}</Badge>
      </button>
      <span class="mx-1">&gt;</span>
      <span class="text-foreground font-medium font-mono">
        {{ truncatedExecutionId }}
      </span>
    </nav>

    <!-- Tab selector -->
    <div class="mb-4 flex items-center gap-4">
      <!-- Primary tabs -->
      <div class="inline-flex rounded-md border border-border">
        <button
          class="px-4 py-2 text-sm font-medium transition-colors rounded-l-md"
          :class="activeTab === 'execution'
            ? 'bg-primary text-primary-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground'"
          @click="activeTab = 'execution'; subView = 'overview'"
        >
          Execution
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors rounded-r-md"
          :class="activeTab === 'summary'
            ? 'bg-primary text-primary-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground'"
          @click="activeTab = 'summary'; subView = 'overview'"
        >
          Summary
        </button>
      </div>

      <!-- Sub-view toggle -->
      <div class="inline-flex rounded-md border border-border">
        <button
          class="px-3 py-1.5 text-xs font-medium transition-colors rounded-l-md"
          :class="subView === 'overview'
            ? 'bg-muted text-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground'"
          @click="subView = 'overview'"
        >
          Overview
        </button>
        <button
          class="px-3 py-1.5 text-xs font-medium transition-colors rounded-r-md"
          :class="subView === 'json'
            ? 'bg-muted text-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground'"
          @click="subView = 'json'"
        >
          JSON
        </button>
      </div>

      <!-- Spacer -->
      <div class="flex-1" />

      <!-- Prev/Next navigation -->
      <div v-if="executionIds.length > 1" class="inline-flex items-center gap-2">
        <button
          class="rounded-md border border-border p-1.5 transition-colors"
          :class="hasPrev ? 'hover:bg-muted text-foreground' : 'text-muted-foreground opacity-40 cursor-not-allowed'"
          :disabled="!hasPrev"
          @click="handlePrev"
        >
          <ChevronLeft class="h-4 w-4" />
        </button>
        <span class="text-xs text-muted-foreground tabular-nums">{{ positionLabel }}</span>
        <button
          class="rounded-md border border-border p-1.5 transition-colors"
          :class="hasNext ? 'hover:bg-muted text-foreground' : 'text-muted-foreground opacity-40 cursor-not-allowed'"
          :disabled="!hasNext"
          @click="handleNext"
        >
          <ChevronRight class="h-4 w-4" />
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="space-y-3">
      <Skeleton class="h-6 w-1/3" />
      <Skeleton class="h-4 w-2/3" />
      <Skeleton class="h-4 w-1/2" />
      <Skeleton class="h-4 w-3/4" />
      <Skeleton class="h-4 w-1/2" />
      <Skeleton class="h-4 w-2/3" />
      <Skeleton class="h-4 w-1/3" />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-950">
      <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
    </div>

    <!-- Execution Tab Content -->
    <div v-else-if="execution && activeTab === 'execution'">
      <!-- JSON sub-view -->
      <div v-if="subView === 'json'" class="rounded-lg border border-border p-4 overflow-x-auto">
        <pre class="text-xs font-mono text-foreground whitespace-pre-wrap break-all">{{ JSON.stringify(rawJson, null, 2) }}</pre>
      </div>

      <!-- Overview sub-view -->
      <ExecutionRecordPanel v-else :execution="execution" :tenant-id="tenantId" :user-id="userId" />
    </div>

    <!-- Summary Tab Content -->
    <div v-else-if="execution && activeTab === 'summary'">
      <!-- JSON sub-view -->
      <div v-if="subView === 'json'" class="rounded-lg border border-border p-4 overflow-x-auto">
        <pre class="text-xs font-mono text-foreground whitespace-pre-wrap break-all">{{ JSON.stringify(rawJson, null, 2) }}</pre>
      </div>

      <!-- Overview sub-view -->
      <ExecutionSummaryPanel v-else :execution="execution" />
    </div>
  </div>
</template>
